import { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/shared/data-table/data-table-column-header'
import { format } from 'date-fns'
import { useLanguage } from '@/context/LanguageContext'
import { ContentBlock, ContentBlockType } from '../types'

const typeLabels: Record<ContentBlockType, string> = {
  only_text: 'Text Only',
  only_image: 'Image Only',
  text_and_image: 'Text & Image',
  only_video: 'Video Only',
  text_and_video: 'Text & Video',
}

const typeVariants: Record<ContentBlockType, 'default' | 'secondary' | 'outline'> = {
  only_text: 'secondary',
  only_image: 'default',
  text_and_image: 'default',
  only_video: 'outline',
  text_and_video: 'outline',
}

function NameCell({ row }: { row: { original: ContentBlock } }) {
  const { language } = useLanguage()
  const profiles = row.original.content_block_profiles
  const profile = profiles.find(p => p.language === language) || profiles[0]

  return (
    <div className="flex space-x-2">
      <span className="max-w-[500px] truncate font-medium">
        {profile?.name}
      </span>
    </div>
  )
}

export const columns: ColumnDef<ContentBlock>[] = [
  {
    accessorKey: 'id',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Id" />
    ),
    size: 10,
    maxSize: 10,
  },
  {
    id: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }) => <NameCell row={row} />,
  },
  {
    accessorKey: 'type',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    cell: ({ row }) => {
      const type = row.getValue('type') as ContentBlockType
      return (
        <Badge variant={typeVariants[type] || 'secondary'}>
          {typeLabels[type] || type}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => {
      const date = row.getValue('createdAt') as string
      if (!date) return null
      return (
        <div className="flex w-[150px] items-center">
          {format(new Date(date), 'MMM d, yyyy')}
        </div>
      )
    },
  },
]
